import React, { useContext, useEffect, useState } from "react"
import tw from "twin.macro"
import { QuestionProps } from "../../../pages/q/[id]"
import dynamic from "next/dynamic"
import { Button } from "../../formElements/button"
import { Routes } from "../../../routes"
import { AuthContext } from "../../../utils/context"
import { useTranslation } from "next-i18next"
import useNoSleep from "../../../utils/hooks/useNoSleep"
import { Container } from "../../page/gridContainer"
import Link from "next/link"
import { Events, registerEvent } from "../../../analytics"
import { QuestionCard } from "../../questionCard"
import { PreviousNextControl } from "../previousNextControl"
import {
  QuestionListNavigation,
  QuestionState,
  useGetQuestionListNavigationLazyQuery,
} from "../../../graphql/generated/graphql"

import { useEffectAsync } from "../../../utils/hooks/useEffectAsync"

const Recorder = dynamic(() => import("../../recorder"), { ssr: false })

export const QuestionOpen = (props: QuestionProps) => {
  const { question, referringProfileUsername } = props
  const { userId } = useContext(AuthContext)
  const { Artist } = question
  const [questionListNav, setQuestionListNav] = useState<QuestionListNavigation | undefined | null>()
  // const questionPrice = supportPayments[0].price
  const isArtist = userId === Artist.id
  const [showRecorder, setShowRecorder] = useState(false)
  const [, enabledNoSleep] = useNoSleep()
  const [getNavigation] = useGetQuestionListNavigationLazyQuery()
  const { t } = useTranslation("question")

  useEffect(() => {
    registerEvent(Events.VisitedOpenQuestion)
  }, [])

  useEffectAsync(async () => {
    const questionListNavResponse = await getNavigation({
      variables: {
        username: referringProfileUsername,
        state: QuestionState.Open,
        currentQuestionId: question.id,
      },
    })
    if (questionListNavResponse && questionListNavResponse.data) {
      setQuestionListNav(questionListNavResponse.data.QuestionListNavigation as QuestionListNavigation)
    }
  }, [])

  if (showRecorder) {
    return <Recorder {...props} />
  }

  const AnswerButton = () => {
    return (
      <>
        {isArtist && (
          <div
            tw="w-full flex flex-col items-center space-y-6 py-6 px-8 tablet:(py-6 flex-row space-y-0 space-x-6 justify-end) bg-background rounded-lg text-white"
            data-theme="dark"
          >
            {/* <span tw="font-bold text-xl">{`$${questionPrice}`}</span> */}
            <div tw="w-full tablet:(w-32)">
              <Button
                twStyle={tw`w-full`}
                dataTestId="answer-button"
                action={t("answer")}
                onClick={async () => {
                  enabledNoSleep(true)
                  setShowRecorder(true)
                  registerEvent(Events.ClickedAnswerQuestion)
                }}
              />
            </div>
          </div>
        )}
      </>
    )
  }

  return (
    <>
      <section tw="py-8 mb-12 h-full">
        <Container>
          {questionListNav && questionListNav.TotalCount > 1 && (
            <PreviousNextControl
              {...{
                currentQuestion: question,
                nextQuestionId: questionListNav.NextQuestion?.id,
                previousQuestionId: questionListNav.PreviousQuestion?.id,
                index: questionListNav.Index,
              }}
            />
          )}
          <div tw="grid grid-cols-4 tablet:grid-cols-12 gap-4 desktop:gap-x-6 gap-y-4">
            <div tw="col-span-4 order-2 space-y-8 tablet:(col-span-8 col-start-3) tablet-lg:(col-span-8 col-start-3)">
              <h1 tw="py-2 text-xl font-bold text-center">
                {t("open.title")}
                <Link href={{ pathname: Routes.Profile, query: { username: Artist.username } }} passHref>
                  <a data-testid="artist-profile-link" tw="block tablet:(inline)">
                    {Artist.name}
                  </a>
                </Link>
              </h1>
            </div>

            <div tw="col-span-4 order-3 tablet:(col-span-8 col-start-3) tablet-lg:(col-span-8 col-start-3 space-y-6)">
              <QuestionCard {...props} linked={false} />
            </div>

            <div tw="col-span-4 order-4 flex flex-col items-center space-y-8 tablet:(col-span-8 col-start-3 items-start) tablet-lg:(col-span-8 col-start-3 space-y-6)">
              <AnswerButton />
            </div>
          </div>
        </Container>
      </section>
    </>
  )
}
